import { TFunction } from 'i18next';
import { AxiosError } from 'axios';
import { addDays, addYears, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import * as Yup from 'yup';
import { ApiScopeDto } from '@api/models/apiClient/ApiScopeDto';
import { ApiClientKeyDto } from '@api/models/apiClient/ApiClientKeyDto';
import { ApiClientDetailDto } from '@api/models/apiClient/ApiClientDetailDto';
import { ApiClientKeyCreateDto } from '@api/models/apiClient/ApiClientKeyCreateDto';
import { ApiAuthMethod } from '@api/models/enums/ApiAuthMethod';
import { saveBlob } from '../../utils/download';
import { getApiErrorCode } from '../../utils/error';
import { normalizeForSearch } from '../../utils/format';
import { Resource } from '@api/resources';
import { apiMessagesStore } from '@api/utils/ApiMessagesStore';

//The API clients live in the "API access" tab of the Integration section.
export const API_ACCESS_PATH = '/integration/api-access';

export const MAX_IP_RANGES = 20;
export const MAX_ACTIVE_KEYS = 5;
const EXPIRY_WARNING_DAYS = 30;
export const MAX_KEY_EXPIRY_YEARS = 3;

export const OPENSSL_COMMANDS = `openssl genpkey -algorithm EC -pkeyopt ec_paramgen_curve:P-256 -out s4e-private.pem
openssl pkey -in s4e-private.pem -pubout -out s4e-public.pem`;

export const isExpired = (value?: string | null) => !!value && !isBefore(new Date(), parseISO(value));
export const isExpiringSoon = (value?: string | null) =>
  !!value && !isExpired(value) && isBefore(parseISO(value), addDays(new Date(), EXPIRY_WARNING_DAYS));

//The form works with a date (yyyy-MM-dd), the API with an instant: the key stops working when the chosen day starts.
export const expiryToApi = (date?: string | null) => (date ? startOfDay(parseISO(date)).toISOString() : null);

//The BE limits the number of unrevoked keys, expired ones included.
export const getUnrevokedKeys = (keys: ApiClientKeyDto[]) => keys.filter((key) => !key.revokedAt);
export const getWorkingKeys = (keys: ApiClientKeyDto[]) => getUnrevokedKeys(keys).filter((key) => !isExpired(key.expiresAt));

const getSortedExpiries = (keys: ApiClientKeyDto[]) =>
  keys.flatMap((key) => key.expiresAt ?? []).sort((a, b) => parseISO(a).getTime() - parseISO(b).getTime());

/** The first expiry among the working keys (null = they never expire). An expired key doesn't count while another one works; with no working key left, the most recent expiry. */
export const getNearestKeyExpiry = (keys: ApiClientKeyDto[]) => {
  const workingKeys = getWorkingKeys(keys);
  if (workingKeys.length > 0) return getSortedExpiries(workingKeys)[0] ?? null;
  return getSortedExpiries(getUnrevokedKeys(keys)).pop() ?? null;
};

// ---- scopes -------------------------------------------------------------

//The catalogue grows on the BE, so an untranslated resource or access falls back to its technical name.
export const getScopeResourceLabel = (t: TFunction, resource: string) =>
  t(`integration.apiAccess.scopes.resources.${resource}`, { defaultValue: resource });
export const getScopeAccessLabel = (t: TFunction, access: string) => t(`integration.apiAccess.scopes.access.${access}`, { defaultValue: access });

export const getScopeResources = (catalogue: ApiScopeDto[]) => [...new Set(catalogue.map((x) => x.resource))];
export const getScopeAccesses = (catalogue: ApiScopeDto[]) => [...new Set(catalogue.map((x) => x.access))];

/** Resources whose label or technical name contains the query, ignoring case and diacritics. */
export const filterScopeResources = (t: TFunction, resources: string[], query: string) => {
  const needle = normalizeForSearch(query.trim());
  return resources.filter((resource) => [getScopeResourceLabel(t, resource), resource].some((x) => normalizeForSearch(x).includes(needle)));
};

/** "Supplier employees: read, write" - one entry per resource, in the catalogue's order. */
export const formatScopes = (t: TFunction, scopes: string[], catalogue: ApiScopeDto[]) => {
  const known = catalogue.filter((x) => scopes.includes(x.scope));
  const unknown = scopes.filter((scope) => !catalogue.some((x) => x.scope === scope));

  const byResource = getScopeResources(known).map(
    (resource) =>
      `${getScopeResourceLabel(t, resource)}: ${known
        .filter((x) => x.resource === resource)
        .map((x) => getScopeAccessLabel(t, x.access).toLowerCase())
        .join(', ')}`,
  );

  return [...byResource, ...unknown];
};

/** "Supplier employees: write" - a single scope as the matrix shows it; an unknown scope falls back to its technical name. */
export const getScopeLabel = (t: TFunction, scope: string, catalogue: ApiScopeDto[]) => {
  const item = catalogue.find((x) => x.scope === scope);
  return item ? `${getScopeResourceLabel(t, item.resource)}: ${getScopeAccessLabel(t, item.access).toLowerCase()}` : scope;
};

export interface MissingScopeRequirement {
  scope: string;
  missing: string[];
}

export interface MissingScopeRequirementGroup {
  scopes: string[];
  missing: string[];
}

/** Granted scopes whose required scopes aren't all granted - the BE rejects these (error 299). */
export const getMissingRequirements = (scopes: string[], catalogue: ApiScopeDto[]): MissingScopeRequirement[] =>
  scopes
    .map((scope) => ({ scope, missing: (catalogue.find((x) => x.scope === scope)?.requires ?? []).filter((required) => !scopes.includes(required)) }))
    .filter((x) => x.missing.length > 0);

/** Requirements with the same missing scopes merged into one, so the message doesn't repeat them. */
export const groupMissingRequirements = (requirements: MissingScopeRequirement[]): MissingScopeRequirementGroup[] => {
  const groups = new Map<string, MissingScopeRequirementGroup>();
  requirements.forEach(({ scope, missing }) => {
    const key = [...missing].sort((a, b) => a.localeCompare(b)).join();
    const group = groups.get(key);
    if (group) group.scopes.push(scope);
    else groups.set(key, { scopes: [scope], missing });
  });
  return [...groups.values()];
};

/** The same message for the FE check and for the BE error 299, with the scopes labelled as in the matrix. */
export const formatMissingRequirement = (t: TFunction, { scopes, missing }: MissingScopeRequirementGroup, catalogue: ApiScopeDto[]) =>
  t('errorCodes.299', {
    scope: scopes.map((x) => t('integration.apiAccess.scopes.quoted', { label: getScopeLabel(t, x, catalogue) })).join(', '),
    missing: missing.map((x) => getScopeLabel(t, x, catalogue)).join(', '),
  });

/** Shows the BE error 299 (its resource leaves it to the form) with the scopes labelled as in the matrix. */
export const showMissingRequirementError = (t: TFunction, error: unknown, resource: Resource, catalogue: ApiScopeDto[]) => {
  if (getApiErrorCode(error) !== '299') return;
  const requirement = (error as AxiosError<{ parameters: MissingScopeRequirement }>).response!.data.parameters;
  apiMessagesStore.setError(resource, formatMissingRequirement(t, { scopes: [requirement.scope], missing: requirement.missing }, catalogue), false);
};

export const scopesValidation = (t: TFunction, catalogue: ApiScopeDto[]) =>
  Yup.array()
    .of(Yup.string().required())
    .min(1, t('errorCodes.273')!)
    .test('scopeRequirements', (scopes, ctx) => {
      const [firstMissing] = groupMissingRequirements(getMissingRequirements(scopes ?? [], catalogue));
      return firstMissing ? ctx.createError({ message: formatMissingRequirement(t, firstMissing, catalogue) }) : true;
    });

// ---- IP ranges ----------------------------------------------------------

const IPV4_REGEX = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const isIpv6 = (address: string) => {
  if (!address.includes(':')) return false;
  try {
    new URL(`http://[${address}]`);
    return true;
  } catch {
    return false;
  }
};

/** The 8 groups of an IPv6 literal, read from the URL parser's canonical form (compressed, IPv4 part as hex). */
const getIpv6Groups = (address: string) => {
  const hostname = new URL(`http://[${address}]`).hostname.slice(1, -1);
  const [head, tail] = hostname.split('::');
  const headGroups = head ? head.split(':') : [];
  const tailGroups = tail ? tail.split(':') : [];
  const zeroGroups = tail === undefined ? [] : new Array(8 - headGroups.length - tailGroups.length).fill('0');
  return [...headGroups, ...zeroGroups, ...tailGroups].map((group) => Number.parseInt(group, 16));
};

//Private, loopback, link-local and unspecified addresses - the same set as the BE (Java InetAddress).
const isPrivateIpv4 = ([a, b, c, d]: number[]) =>
  a === 10 ||
  (a === 172 && b >= 16 && b <= 31) ||
  (a === 192 && b === 168) ||
  a === 127 ||
  (a === 169 && b === 254) ||
  (a === 0 && b === 0 && c === 0 && d === 0);

const isPrivateIpv6 = (groups: number[]) => {
  //IPv4-mapped (::ffff:10.0.0.1) - Java treats it as the IPv4 address.
  if (groups.slice(0, 5).every((group) => group === 0) && groups[5] === 0xffff) {
    return isPrivateIpv4([groups[6] >> 8, groups[6] & 0xff, groups[7] >> 8, groups[7] & 0xff]);
  }
  const isUnspecifiedOrLoopback = groups.slice(0, 7).every((group) => group === 0) && groups[7] <= 1;
  const isLinkLocal = (groups[0] & 0xffc0) === 0xfe80;
  const isSiteLocal = (groups[0] & 0xffc0) === 0xfec0;
  const isUniqueLocal = (groups[0] & 0xfe00) === 0xfc00;
  return isUnspecifiedOrLoopback || isLinkLocal || isSiteLocal || isUniqueLocal;
};

const parseIpAddress = (address: string) => {
  if (IPV4_REGEX.test(address)) return { maxPrefix: 32, isPrivate: isPrivateIpv4(address.split('.').map(Number)) };
  if (isIpv6(address)) return { maxPrefix: 128, isPrivate: isPrivateIpv6(getIpv6Groups(address)) };
  return null;
};

const parseIpRange = (value: string) => {
  const [address, prefix, ...rest] = value.trim().split('/');
  if (rest.length > 0 || (prefix !== undefined && !/^\d{1,3}$/.test(prefix))) return null;

  const parsed = parseIpAddress(address);
  if (!parsed) return null;
  if (prefix !== undefined && Number(prefix) > parsed.maxPrefix) return null;

  return { prefix: prefix === undefined ? parsed.maxPrefix : Number(prefix), isPrivate: parsed.isPrivate };
};

//Mirrors the BE (IpAddresses.normalizeRange) for UX, in the same order: malformed, too broad, private.
export const ipRangesValidation = (t: TFunction) =>
  Yup.array()
    .of(
      Yup.string()
        .trim()
        .required(t('errors.required')!)
        .test('ipRange', (value, ctx) => {
          const range = parseIpRange(value ?? '');
          if (!range) return ctx.createError({ message: t('errorCodes.274', { value }) });
          if (range.prefix === 0) return ctx.createError({ message: t('errorCodes.275', { value }) });
          if (range.isPrivate) return ctx.createError({ message: t('errorCodes.276', { value }) });
          return true;
        }),
    )
    .max(MAX_IP_RANGES, t('errorCodes.277', { max: MAX_IP_RANGES })!);

// ---- keys ---------------------------------------------------------------

export interface KeyFormValues {
  //Chosen when creating the API access; a new key is always of the API access's method.
  authMethod: ApiAuthMethod;
  //Signing key only.
  publicKeyPem: string;
  //Only when the admin explicitly chose it - an expiry is the default.
  keyNeverExpires: boolean;
  keyExpiresAt: string | null;
}

const getMaxKeyExpiry = () => startOfDay(addYears(new Date(), MAX_KEY_EXPIRY_YEARS));

export const getKeyFormInitialValues = (): KeyFormValues => ({
  //The recommended, more secure method.
  authMethod: ApiAuthMethod.SigningKey,
  publicKeyPem: '',
  keyNeverExpires: false,
  //The admin picks the date - nothing is chosen for them.
  keyExpiresAt: null,
});

export const keyExpiryToApi = (values: KeyFormValues) => (values.keyNeverExpires ? null : expiryToApi(values.keyExpiresAt));

/** For the date picker - only the days the BE accepts: after today and at most MAX_KEY_EXPIRY_YEARS ahead. */
export const isKeyExpiryDisabled = (day: Date) => !isBefore(new Date(), startOfDay(day)) || isAfter(startOfDay(day), getMaxKeyExpiry());

export const expiryValidation = (t: TFunction) =>
  Yup.string()
    .nullable()
    .test('expiryInFuture', t('errorCodes.279')!, (date) => !date || isBefore(new Date(), startOfDay(parseISO(date))))
    .test(
      'expiryWithinMax',
      t('errorCodes.300', { maxYears: MAX_KEY_EXPIRY_YEARS })!,
      (date) => !date || !isAfter(startOfDay(parseISO(date)), getMaxKeyExpiry()),
    );

/** Looks like a PEM public key - the BE checks it is EC P-256. */
export const isPublicKeyPem = (value?: string) => {
  const pem = value?.trim() ?? '';
  return pem.startsWith('-----BEGIN PUBLIC KEY-----') && pem.endsWith('-----END PUBLIC KEY-----') && !pem.includes('PRIVATE');
};

export const keyFormValidation = (t: TFunction) => ({
  publicKeyPem: Yup.string().when('authMethod', {
    is: ApiAuthMethod.SigningKey,
    then: (schema) => schema.trim().required(t('errors.required')!).test('publicKey', t('errorCodes.278')!, isPublicKeyPem),
  }),
  keyExpiresAt: expiryValidation(t).when('keyNeverExpires', { is: false, then: (schema) => schema.required(t('errors.required')!) }),
});

/** A key of the API access's method: an API key is generated by the BE, so it needs only the expiry. */
export const toApiClientKeyCreateDto = (values: KeyFormValues): ApiClientKeyCreateDto => ({
  publicKeyPem: values.authMethod === ApiAuthMethod.SigningKey ? values.publicKeyPem.trim() : undefined,
  expiresAt: keyExpiryToApi(values),
});

/** What the partner needs to connect; the API key only right after it was created - it is never shown again. */
export interface IntegrationCredentials extends Pick<ApiClientDetailDto, 'clientId' | 'tokenEndpoint' | 'apiBase' | 'docsUrl'> {
  clientName: string;
  kid: string;
  apiKey?: string;
}

//No api_base - the docs show it with every endpoint; only the credentials file has it, for the partner's code.
export type ConnectionDetails = Pick<IntegrationCredentials, 'clientId' | 'tokenEndpoint' | 'docsUrl'> & { kid?: string };

interface ConnectionRow {
  label: string;
  value: string;
}

/** Labelled by the names integrators know from the credentials file; `kid` only when one key is meant, URLs only when configured. */
export const getConnectionRows = ({ clientId, kid, tokenEndpoint, docsUrl }: ConnectionDetails) =>
  [
    { label: 'client_id', value: clientId },
    { label: 'kid', value: kid },
    { label: 'token_endpoint', value: tokenEndpoint },
    { label: 'docs', value: docsUrl },
  ].filter((row): row is ConnectionRow => !!row.value);

//Values aligned in one column, as the longest label is 'token_endpoint:'.
export const formatConnectionText = (details: ConnectionDetails) =>
  getConnectionRows(details)
    .map(({ label, value }) => `${label}:`.padEnd(16) + value)
    .join('\n');

export const downloadCredentialsFile = (credentials: IntegrationCredentials) => {
  //Undefined leaves the key out of the JSON, like the missing lines in the connection text.
  const fileContent = {
    client_id: credentials.clientId,
    kid: credentials.kid,
    client_secret: credentials.apiKey,
    token_endpoint: credentials.tokenEndpoint ?? undefined,
    api_base: credentials.apiBase ?? undefined,
    docs: credentials.docsUrl ?? undefined,
  };
  const clientName =
    normalizeForSearch(credentials.clientName)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'integration';

  saveBlob(new Blob([JSON.stringify(fileContent, null, 2)], { type: 'application/json' }), `s4e-${clientName}-${credentials.kid}.json`);
};
